import React, { useState } from 'react';
import {
    ExternalLink,
    Link as LinkIcon,
    Download,
    MoreVertical,
    PencilLine,
    FolderInput,
    Copy,
    Trash2,
} from 'lucide-react';
import { Button } from '@rocco/components/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@rocco/components/popover';
import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { useRenameModal } from './rename-modal-atom';
import { toast } from 'sonner';
import { useDesignBoardPageState } from '@modules/design-board/component/context';
import { useBuildHubData } from '@modules/build-hub/hooks/use-build-hub-data';
import { useTracker } from '@archipro-website/tracker';

interface PinOptionsMenuProps {
    pin: DetailedPinWithChecked;
    canEdit: boolean;
    boardLink: string;
    boardId?: number;
    onDelete?: () => void;
}

/**
 * Options menu for moodboard pins with actions like rename, copy link, download, etc.
 */
export const PinOptionsMenu: React.FC<PinOptionsMenuProps> = ({
    pin,
    canEdit,
    boardLink,
    boardId,
    onDelete,
}) => {
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const { openModal } = useRenameModal();
    const { dispatch } = useDesignBoardPageState();
    const { buildHub } = useBuildHubData();
    const tracker = useTracker();

    // Determine pin type

    const isMemberUpload = pin.Target?.__typename === 'MemberUpload';
    const isLibraryImage = pin.Target?.__typename === 'LibraryImage';

    // Get source link for library images
    const sourceLink =
        pin.Target?.__typename === 'LibraryImage'
            ? pin.Target.LibraryItem?.Link
            : null;

    // Get download link for member uploads
    const downloadLink = isMemberUpload ? pin.Link : null;

    const handleCopyLink = (e: React.MouseEvent) => {
        e.stopPropagation();
        const windowOrigin = window.location.origin;
        const url = `${windowOrigin}${boardLink}?item=${pin.ID}`;
        navigator.clipboard
            .writeText(url)
            .then(() => {
                toast.success('Link copied to clipboard');
                setIsPopoverOpen(false);
            })
            .catch(() => {
                toast.error('Failed to copy link');
            });
    };

    const handleDownload = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (downloadLink) {
            // Get the original filename with extension from the upload
            const originalFilename =
                pin.Target?.__typename === 'MemberUpload'
                    ? pin.Target.FileName
                    : pin.Title;

            // Create a temporary anchor element to trigger download
            const link = document.createElement('a');
            link.href = downloadLink;
            link.download = originalFilename || 'download';
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        setIsPopoverOpen(false);
    };

    const handleViewSource = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (sourceLink) {
            window.open(sourceLink, '_blank');
        }
        setIsPopoverOpen(false);
    };

    const handleRenameClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        openModal(pin);
        setIsPopoverOpen(false);
    };

    const handleMoveClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (boardId === undefined) return;

        dispatch({
            type: 'OpenMovePinsDrawer',
            pinMoveData: {
                pinListString: String(pin.ID),
                boardId,
                buildHubUrl: buildHub.urlSegment,
            },
        });
        setIsPopoverOpen(false);
    };

    const handleCopyClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (boardId === undefined) return;

        dispatch({
            type: 'OpenCopyPinsDrawer',
            pinMoveData: {
                pinListString: String(pin.ID),
                boardId,
                buildHubUrl: buildHub.urlSegment,
            },
        });
        setIsPopoverOpen(false);
    };

    const handleDeleteClick = (e: React.MouseEvent) => {
        e.stopPropagation();

        tracker.log('PinMenuDelete', {
            label: pin.professionalTitle || '',
            url: new URL(window.location.href),
            data: {
                ItemType: 'pin',
                ItemID: [pin.ID],
            },
        });

        onDelete?.();
        setIsPopoverOpen(false);
    };

    // Build actions array
    const actions = [];

    if (isLibraryImage && sourceLink) {
        actions.push({
            label: 'View source',
            icon: <ExternalLink className="h-4 w-4" />,
            onClick: handleViewSource,
        });
        actions.push({
            type: 'separator',
        });
    }

    if (canEdit) {
        actions.push({
            label: 'Rename',
            icon: <PencilLine className="h-4 w-4" />,
            onClick: handleRenameClick,
        });
        actions.push({
            type: 'separator',
        });
    }

    actions.push({
        label: 'Copy link',
        icon: <LinkIcon className="h-4 w-4" />,
        onClick: handleCopyLink,
    });

    if (isMemberUpload && downloadLink) {
        actions.push({
            label: 'Download',
            icon: <Download className="h-4 w-4" />,
            onClick: handleDownload,
        });
    }

    // Move, Copy, Delete actions (only if canEdit)
    if (canEdit) {
        // Only add separator and actions if we have at least one action to show
        const hasActionToShow = boardId !== undefined || onDelete;

        if (hasActionToShow) {
            actions.push({
                type: 'separator',
            });
        }

        // Only show Move/Copy if boardId is available
        if (boardId !== undefined) {
            actions.push({
                label: 'Move',
                icon: <FolderInput className="h-4 w-4" />,
                onClick: handleMoveClick,
            });

            actions.push({
                label: 'Copy',
                icon: <Copy className="h-4 w-4" />,
                onClick: handleCopyClick,
            });
        }

        // Only add separator before delete if we had move/copy
        if (boardId !== undefined && onDelete) {
            actions.push({
                type: 'separator',
            });
        }

        if (onDelete) {
            actions.push({
                label: 'Delete',
                icon: <Trash2 className="h-4 w-4" />,
                onClick: handleDeleteClick,
                variant: 'destructive',
            });
        }
    }

    return (
        <>
            <div
                onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
            >
                <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            color="secondary"
                            size="square42"
                            aria-label="Menu"
                            title="Menu"
                            className="transition-all duration-100 hover:bg-gray-200"
                        >
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="end"
                        paddings="xsmall"
                        variant="white"
                        background="white"
                    >
                        <div className="flex min-w-60 flex-col space-y-1">
                            {actions.map((action, index) => {
                                if (action.type === 'separator') {
                                    return (
                                        <div
                                            key={`separator-${index}`}
                                            className="h-px w-full bg-gray-200"
                                        />
                                    );
                                }
                                return (
                                    <button
                                        key={`${action.label}-${index}`}
                                        className={`group flex cursor-pointer items-center justify-start space-x-2 rounded-lg px-2 py-2 text-sm transition-colors ${
                                            action.variant === 'destructive'
                                                ? 'text-red-600 hover:bg-red-50'
                                                : 'hover:bg-gray-100'
                                        }`}
                                        onClick={action.onClick}
                                    >
                                        {action.icon}
                                        <span>{action.label}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
        </>
    );
};
